/**
 * Endpoint agent control: tamper protection and agent releases (auto-update).
 * Backed by services/firewall routes/agentControlRoutes.ts. Rendered on the Endpoint Agents page.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './ui';
import { parseServerDate } from '../i18n/format';

interface Release {
  id: string; version: string; os: string; arch: string; sha256: string; size: number;
  notes: string | null; status: 'active' | 'paused'; rollout_pct: number; created_at: string;
}

const fmtSize = (n: number) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.round(n / 1e3)} KB`);

const AgentControlPanel: React.FC<{ apiUrl: string }> = ({ apiUrl }) => {
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`components.agentControl.${k}`, { defaultValue: d });
  const [tamper, setTamper] = useState<{ enabled: boolean; passwordSet: boolean } | null>(null);
  const [password, setPassword] = useState('');
  const [autoUpdate, setAutoUpdate] = useState(true);
  const [releases, setReleases] = useState<Release[]>([]);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [form, setForm] = useState({ version: '', os: 'windows', arch: 'amd64', rollout: '100', notes: '' });
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const json = async (url: string, init?: RequestInit) => {
    const r = await fetch(`${apiUrl}${url}`, init);
    const body = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(body.error || body.message || `HTTP ${r.status}`);
    return body;
  };
  const load = useCallback(async () => {
    try {
      setTamper(await json('/admin/bdr/tamper-protection'));
      setAutoUpdate((await json('/admin/bdr/auto-update')).enabled);
      setReleases((await json('/admin/bdr/releases')).releases || []);
    } catch (e: any) { setMsg({ ok: false, text: e.message }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn: () => Promise<any>, okText: string) => {
    setBusy(true); setMsg(null);
    try { await fn(); setMsg({ ok: true, text: okText }); await load(); }
    catch (e: any) { if (e?.name !== 'AbortError') setMsg({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  };

  const saveTamper = (enabled: boolean) => run(async () => {
    await json('/admin/bdr/tamper-protection', {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enabled, ...(password ? { password } : {}) }),
    });
    setPassword('');
  }, enabled ? tt('tamperOn', 'Tamper protection is on. Devices pick it up within about five minutes.') : tt('tamperOff', 'Tamper protection is off.'));

  const upload = () => run(async () => {
    if (!file) throw new Error(tt('chooseFile', 'Choose the installer file first'));
    const q = new URLSearchParams({ version: form.version.trim(), os: form.os, arch: form.arch, rollout: form.rollout, notes: form.notes });
    await json(`/admin/bdr/releases/upload?${q}`, { method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: file });
    setFile(null); setForm({ ...form, version: '', notes: '' });
  }, tt('uploaded', 'Release uploaded. Devices on an older version will update within about five minutes (within the rollout percentage).'));

  const patch = (id: string, body: object) => run(
    () => json(`/admin/bdr/releases/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    tt('saved', 'Saved.'));
  const remove = (id: string) => run(() => json(`/admin/bdr/releases/${id}`, { method: 'DELETE' }), tt('deleted', 'Release deleted.'));

  return (
    <>
      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">
          <span><Icon name="shield" /> {tt('tamperTitle', 'Tamper protection')}</span>
          <span style={{ color: 'var(--text-2)', fontSize: 12 }}>
            {tt('tamperHint', 'Stops local administrators from stopping or removing the agent service. Stopping or uninstalling then needs this password.')}
          </span>
        </div>
        <div style={{ padding: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="ent-badge">{tamper?.enabled ? tt('on', 'On') : tt('off', 'Off')}</span>
          <input
            className="ent-input" type="password" autoComplete="new-password" style={{ maxWidth: 260 }}
            placeholder={tamper?.passwordSet ? tt('pwKeep', 'New password (blank keeps the current one)') : tt('pwSet', 'Tamper-protection password (8+ characters)')}
            value={password} onChange={(e) => setPassword(e.target.value)}
          />
          <button className="ent-btn primary" disabled={busy} onClick={() => saveTamper(true)}>
            {tamper?.enabled ? tt('update', 'Update') : tt('enable', 'Enable')}
          </button>
          {tamper?.enabled && <button className="ent-btn" disabled={busy} onClick={() => saveTamper(false)}>{tt('disable', 'Disable')}</button>}
        </div>
      </div>

      <div className="ent-card" style={{ marginBottom: 16 }}>
        <div className="ent-card-head">
          <span><Icon name="download" /> {tt('updateTitle', 'Agent updates')}</span>
          <label style={{ fontSize: 12, color: 'var(--text-2)', display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={autoUpdate} disabled={busy}
              onChange={(e) => run(() => json('/admin/bdr/auto-update', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ enabled: e.target.checked }) }), tt('saved', 'Saved.'))} />
            {tt('autoUpdate', 'Auto-update enabled (kill switch)')}
          </label>
        </div>
        <div style={{ padding: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <input className="ent-input" style={{ width: 110 }} placeholder="1.3.1" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} />
          <select className="ent-input" style={{ width: 110 }} value={form.os} onChange={(e) => setForm({ ...form, os: e.target.value })}>
            <option value="windows">Windows</option><option value="macos">macOS</option>
          </select>
          <select className="ent-input" style={{ width: 100 }} value={form.arch} onChange={(e) => setForm({ ...form, arch: e.target.value })}>
            <option value="amd64">amd64</option><option value="arm64">arm64</option><option value="any">any</option>
          </select>
          <input className="ent-input" style={{ width: 90 }} type="number" min={0} max={100} title={tt('rollout', 'Rollout %')} value={form.rollout} onChange={(e) => setForm({ ...form, rollout: e.target.value })} />
          <input className="ent-input" style={{ flex: 1, minWidth: 160 }} placeholder={tt('notes', 'Release notes (optional)')} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <input type="file" accept=".exe,.pkg" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <button className="ent-btn primary" disabled={busy || !form.version || !file} onClick={upload}>{tt('upload', 'Upload release')}</button>
        </div>
        {releases.length > 0 && (
          <div className="ent-table-wrap scroll">
            <table className="ent-table">
              <thead><tr><th>{tt('version', 'Version')}</th><th>OS</th><th>{tt('rolloutCol', 'Rollout')}</th><th>{tt('size', 'Size')}</th><th>{tt('status', 'Status')}</th><th>{tt('added', 'Added')}</th><th /></tr></thead>
              <tbody>
                {releases.map((r) => (
                  <tr key={r.id} title={`sha256 ${r.sha256}${r.notes ? `\n${r.notes}` : ''}`}>
                    <td>{r.version}</td><td>{r.os}/{r.arch}</td>
                    <td>
                      <input className="ent-input" style={{ width: 70 }} type="number" min={0} max={100} defaultValue={r.rollout_pct}
                        onBlur={(e) => Number(e.target.value) !== r.rollout_pct && patch(r.id, { rollout_pct: Number(e.target.value) })} /> %
                    </td>
                    <td>{fmtSize(r.size)}</td>
                    <td>{r.status}</td>
                    <td>{parseServerDate(r.created_at).toLocaleString()}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="ent-btn" disabled={busy} onClick={() => patch(r.id, { status: r.status === 'active' ? 'paused' : 'active' })}>
                        {r.status === 'active' ? tt('pause', 'Pause') : tt('resume', 'Resume')}
                      </button>{' '}
                      <button className="ent-btn danger" disabled={busy} onClick={() => remove(r.id)}>{tt('delete', 'Delete')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {msg && <div style={{ padding: '0 14px 14px', color: msg.ok ? 'var(--ok, #2e9e5b)' : 'var(--danger, #d64545)', fontSize: 13 }}>{msg.text}</div>}
      </div>
    </>
  );
};

export default AgentControlPanel;
